import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { wfApi } from '../../api/workflow.js';

export default function WorkflowInstances() {
  const { id } = useParams();
  if (id) return <InstanceDetail id={id} />;
  return <InstanceList />;
}

function InstanceList() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ resource_type: '', status: '' });
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    wfApi.listInstances(params).then((d) => setItems(d?.items || []));
  }, [filters]);

  return (
    <section>
      <header className="page-header"><h1>Workflow Instances</h1></header>
      <div className="card form-card grid-form">
        <label>
          Resource type
          <select value={filters.resource_type} onChange={(e) => setFilters((f) => ({ ...f, resource_type: e.target.value }))}>
            <option value="">All</option>
            <option value="leave_request">leave_request</option>
            <option value="expense">expense</option>
            <option value="purchase_request">purchase_request</option>
            <option value="budget">budget</option>
            <option value="release">release</option>
          </select>
        </label>
        <label>
          Status
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            <option value="in_progress">in_progress</option>
            <option value="approved">approved</option>
            <option value="rejected">rejected</option>
            <option value="cancelled">cancelled</option>
          </select>
        </label>
      </div>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>Resource</th><th>Initiator</th><th>Workflow</th><th>Status</th><th>Started</th></tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id}>
                <td><Link to={`/workflows/instances/${i.id}`}>{i.resource_type} · <code>{i.resource_id?.slice(0, 8)}</code></Link></td>
                <td>{i.initiator_name || '—'}</td>
                <td className="muted">{i.definition_name}</td>
                <td><span className={`chip status-${i.status}`}>{i.status}</span></td>
                <td className="muted">{i.created_at?.slice(0, 16).replace('T', ' ')}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No instances.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InstanceDetail({ id }) {
  const [inst, setInst] = useState(null);
  const [error, setError] = useState(null);
  function refresh() { wfApi.getInstance(id).then((d) => setInst(d?.instance)).catch((e) => setError(e.response?.data?.message || 'Failed')); }
  useEffect(refresh, [id]);

  if (error) return <div className="alert error">{error}</div>;
  if (!inst) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{inst.definition_name}</h1>
          <p className="muted">
            {inst.resource_type} · <code>{inst.resource_id}</code> ·{' '}
            <span className={`chip status-${inst.status}`}>{inst.status}</span>
          </p>
        </div>
        <Link to="/workflows/instances" className="btn ghost">← All instances</Link>
      </header>

      <div className="card">
        <h3>Context</h3>
        <pre className="ctx-pre">{JSON.stringify(inst.context || {}, null, 2)}</pre>
      </div>

      <div className="card">
        <h3>Timeline</h3>
        <ol className="timeline">
          {inst.steps.map((s) => (
            <li key={s.id} className={`tl-${s.status}`}>
              <div className="tl-dot" />
              <div className="tl-content">
                <strong>{s.step_name || s.step_order}</strong>
                <span className={`chip status-${s.status === 'pending' ? 'pending' : s.status === 'approved' ? 'approved' : s.status === 'rejected' ? 'rejected' : 'cancelled'}`}>{s.status}</span>
                <div className="muted small">
                  {s.assignee_name ? `Assigned to ${s.assignee_name}` : 'No assignee'}
                  {s.due_at && ` · due ${new Date(s.due_at).toLocaleString()}`}
                  {s.acted_at && ` · acted ${new Date(s.acted_at).toLocaleString()}`}
                </div>
                {s.comments && <div className="muted small">"{s.comments}"</div>}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
